/*****************************************************************
 *  CUSTOMER BUTLER - FROM THE OTHER BUTLERS                       *
 *  File: apps/web/src/pages/customer/SuiteTimeline.js             *
 *  What it does: two pieces of screen.                            *
 *    SuiteTimelinePanel  on the Activities tab: what the Report   *
 *                        Engine, Dispatch, On-Call, the water     *
 *                        module and Schedule did for this         *
 *                        customer (or property), newest first,    *
 *                        one source chip (word + icon) per row.   *
 *    HoldingListCard     on CRM Setup: events no customer was     *
 *                        found for yet, each with "Attach to a    *
 *                        customer" (managers only).               *
 *  Reads/Writes: /customer/timeline/... (customerApi.js).         *
 *****************************************************************/

/*****************************************************************
 *  TABLE OF CONTENTS                                              *
 *  SECTION 1 - THE WORDS AND ICONS FOR EACH SOURCE                *
 *  SECTION 2 - ONE ENTRY ROW                                      *
 *  SECTION 3 - THE ACTIVITIES TAB PANEL                           *
 *  SECTION 4 - THE ATTACH SHEET                                   *
 *  SECTION 5 - THE HOLDING LIST CARD (CRM Setup)                  *
 *****************************************************************/

/* WHY (2026-10-14, James via Fable; readiness "nice later" 16): a
   customer's Activities tab should show what the suite did for them.
   Honest UI: when nothing has come in, it says so in plain words. */

import React, { useCallback, useEffect, useState } from 'react';
import { Icon } from './customerIcons';
import { QuietLine, Notice, Sheet, StatusChip } from './customerParts';
import { formatMoment } from './customerDates';
// WHY (2026-10-15, BACKLOG 317): a send pulled back shows its own chip.
import { lookForEvent } from './suiteEventLooks';
import { useCustomerAccessView } from './customerAccessView';
import { useButlerLook } from './customerLook';
import {
  fetchSuiteTimeline, fetchPropertySuiteTimeline, fetchHoldingEntries,
  attachHoldingEntry, fetchCustomerPage,
} from './customerApi';
import './customer.css';

/*****************************************************************
 *  SECTION 1 - THE WORDS AND ICONS FOR EACH SOURCE                *
 *****************************************************************/

// One look per source: the chip's word and its thin-line icon.
const SOURCE_LOOKS = Object.freeze({
  'report-engine': { word: 'Report Engine', icon: 'file' },
  dispatch: { word: 'Dispatch', icon: 'truck' },
  oncall: { word: 'On-Call', icon: 'phone' },
  water: { word: 'Water test', icon: 'drop' },
  schedule: { word: 'Schedule', icon: 'calendar' },
});

// Plain words for how the customer was found.
const MATCHED_WORDS = Object.freeze({
  'customer id': 'Matched by customer record',
  email: 'Matched by email',
  address: 'Matched by address',
  'email and address': 'Matched by email and address',
  'attached by hand': 'Attached by hand',
});

/* ---- lookForSource(sourceModule) -- a known look, or a plain one ---- */
function lookForSource(sourceModule) {
  return SOURCE_LOOKS[sourceModule] || { word: 'Another Butler', icon: 'link' };
}

/* ---- isAddressOnThisSite(linkUrl) -- '/app/...' rather than https ---- */
function isAddressOnThisSite(linkUrl) {
  return linkUrl.startsWith('/') && !linkUrl.startsWith('//');
}

/*****************************************************************
 *  SECTION 2 - ONE ENTRY ROW                                      *
 *****************************************************************/

/* ---- EntryName({ entry }) -- the summary, a link when there is one ---- */
function EntryName({ entry }) {
  if (!entry.linkUrl) {
    return <span className="cb-item-name">{entry.summary}</span>;
  }

  const opensNewTab = !isAddressOnThisSite(entry.linkUrl);

  return (
    <a
      className="cb-item-name cb-entry-link"
      href={entry.linkUrl}
      target={opensNewTab ? '_blank' : undefined}
      rel={opensNewTab ? 'noopener noreferrer' : undefined}
    >
      {entry.summary}
    </a>
  );
}

/* ---- SuiteEntryRow({ entry, children }) ------------------------
   One row: the summary (or its link), the one source chip, then
   when, who and how it was matched. children = extra row parts. */
function SuiteEntryRow({ entry, children }) {
  const sourceLook = lookForSource(entry.sourceModule);
  const eventChip = lookForEvent(entry.eventName).chip; // null for most events
  const matchedWords = MATCHED_WORDS[entry.matchedBy] || '';
  const metaWords = [formatMoment(entry.occurredAt), entry.whoName, matchedWords].filter(Boolean).join(', ');

  return (
    <li className="cb-item">
      <div className="cb-item-top">
        <EntryName entry={entry} />
        <StatusChip tone="neutral" icon={sourceLook.icon} word={sourceLook.word} />
        {eventChip ? <StatusChip tone={eventChip.tone} icon={eventChip.icon} word={eventChip.word} /> : null}
      </div>
      <div className="cb-item-meta">{metaWords}</div>
      {entry.attachWhy ? <div className="cb-item-body">{`Why: ${entry.attachWhy}`}</div> : null}
      {children}
    </li>
  );
}

/*****************************************************************
 *  SECTION 3 - THE ACTIVITIES TAB PANEL                           *
 *****************************************************************/

/* ---- SuiteTimelinePanel({ customerId, propertyId }) ------------
   The "From the other Butlers" list for a customer, or for a
   property when propertyId is given.                            */
export function SuiteTimelinePanel({ customerId, propertyId }) {
  const [entries, setEntries] = useState(null);    // null = loading
  const [couldNotLoad, setCouldNotLoad] = useState(false);

  useEffect(() => {
    let isStillShown = true;

    async function loadEntries() {
      try {
        const loadedEntries = propertyId
          ? await fetchPropertySuiteTimeline(propertyId)
          : await fetchSuiteTimeline(customerId);

        if (isStillShown) {
          setEntries(Array.isArray(loadedEntries) ? loadedEntries : []);
        }
      } catch (loadProblem) {
        if (isStillShown) {
          setCouldNotLoad(true);
          setEntries([]);
        }
      }
    }

    loadEntries();

    return () => {
      isStillShown = false;
    };
  }, [customerId, propertyId]);

  let panelBody = null;

  if (couldNotLoad) {
    panelBody = <QuietLine icon="warn">The list from the other Butlers could not be loaded.</QuietLine>;
  } else if (entries === null) {
    panelBody = <QuietLine icon="clock">Loading</QuietLine>;
  } else if (entries.length === 0) {
    panelBody = <QuietLine icon="link">Nothing from the other Butlers yet.</QuietLine>;
  } else {
    panelBody = (
      <ul className="cb-list-rows">
        {entries.map((oneEntry) => <SuiteEntryRow key={oneEntry.id} entry={oneEntry} />)}
      </ul>
    );
  }

  return (
    <section className="cb-suite-timeline" aria-label="From the other Butlers">
      <h3 className="cb-panel-head">From the other Butlers</h3>
      <div className="cb-panel">{panelBody}</div>
    </section>
  );
}

/*****************************************************************
 *  SECTION 4 - THE ATTACH SHEET                                   *
 *  Search for the customer, pick one, say why, attach.            *
 *****************************************************************/

const SHORTEST_SEARCH = 2; // letters typed before the search runs

/* ---- AttachSheet({ entry, onAttached, onClose }) --------------- */
function AttachSheet({ entry, onAttached, onClose }) {
  const [searchWords, setSearchWords] = useState('');
  const [foundCustomers, setFoundCustomers] = useState([]);
  const [chosenCustomer, setChosenCustomer] = useState(null);
  const [whyWords, setWhyWords] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [problemWords, setProblemWords] = useState('');

  useEffect(() => {
    let isStillShown = true;
    const trimmedSearch = searchWords.trim();

    if (trimmedSearch.length < SHORTEST_SEARCH) {
      setFoundCustomers([]);
      return undefined;
    }

    fetchCustomerPage({ search: trimmedSearch, limit: 10 })
      .then((pageAnswer) => {
        if (isStillShown) {
          setFoundCustomers(pageAnswer.rows);
        }
      })
      .catch(() => {
        if (isStillShown) {
          setFoundCustomers([]);
        }
      });

    return () => {
      isStillShown = false;
    };
  }, [searchWords]);

  async function attachIt(submitEvent) {
    submitEvent.preventDefault();

    if (!chosenCustomer) {
      setProblemWords('Choose a customer first.');
      return;
    }

    setIsSaving(true);
    setProblemWords('');

    const attachAnswer = await attachHoldingEntry(entry.id, chosenCustomer.id, whyWords);

    setIsSaving(false);

    if (!attachAnswer.ok) {
      setProblemWords((attachAnswer.error && attachAnswer.error.message) || 'That did not attach. Nothing changed.');
      return;
    }

    onAttached(chosenCustomer);
  }

  return (
    <Sheet title="Attach to a customer" onClose={onClose}>
      <form onSubmit={attachIt}>
        <p className="cb-sub">{entry.summary}</p>
        <label className="cb-label" htmlFor="cb-attach-search">Find the customer</label>
        <input id="cb-attach-search" className="cb-input" type="search" value={searchWords} onChange={(changeEvent) => setSearchWords(changeEvent.target.value)} />
        <ul className="cb-list-rows">
          {foundCustomers.map((oneCustomer) => (
            <li key={oneCustomer.id} className="cb-item">
              <label className="cb-choice">
                <input
                  type="radio"
                  name="cb-attach-customer"
                  checked={Boolean(chosenCustomer) && chosenCustomer.id === oneCustomer.id}
                  onChange={() => setChosenCustomer(oneCustomer)}
                />
                <span className="cb-item-name">{oneCustomer.displayName || 'Unnamed customer'}</span>
              </label>
              <div className="cb-item-meta">{[oneCustomer.primaryEmail, oneCustomer.primaryPhone].filter(Boolean).join(', ')}</div>
            </li>
          ))}
        </ul>
        <label className="cb-label" htmlFor="cb-attach-why">Why (optional)</label>
        <input id="cb-attach-why" className="cb-input" type="text" value={whyWords} onChange={(changeEvent) => setWhyWords(changeEvent.target.value)} />
        {problemWords ? <p className="cb-edit-error" role="alert">{problemWords}</p> : null}
        <div className="cb-sheet-acts">
          <button type="button" className="bc-btn is-ghost" onClick={onClose}>Cancel</button>
          <button type="submit" className="bc-btn is-primary" disabled={isSaving}>{isSaving ? 'Attaching' : 'Attach it'}</button>
        </div>
      </form>
    </Sheet>
  );
}

/*****************************************************************
 *  SECTION 5 - THE HOLDING LIST CARD (CRM Setup)                  *
 *  Shown only to people whose level may change setup (Honest UI:  *
 *  the server refuses everyone else).                             *
 *****************************************************************/

// WHY (2026-10-15, BACKLOG 317): "Events the door refused" (DoorRefusals.js)
// shows the waiting count and a way here; the card has an address for it.
export const HOLDING_CARD_ID = 'cb-holding-card';           // the card's address on the page
const HOLDING_DETAILS_ID = 'cb-holding-card-details';      // its fold, opened from outside

/* ---- openTheHoldingCard() ----------------------------------------
   Opens the waiting list's fold and brings it on screen. Used by
   the "Events the door refused" card. Changes nothing saved.    */
export function openTheHoldingCard() {
  const holdingFold = document.getElementById(HOLDING_DETAILS_ID);
  const holdingCard = document.getElementById(HOLDING_CARD_ID);

  if (holdingFold) {
    holdingFold.open = true;
  }

  if (holdingCard && holdingCard.scrollIntoView) {
    holdingCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

/* ---- HoldingListCard() -- the whole card ---- */
export function HoldingListCard() {
  useButlerLook();

  const accessView = useCustomerAccessView(); // what this person may be shown
  const [entries, setEntries] = useState(null);
  const [entryBeingAttached, setEntryBeingAttached] = useState(null);
  const [noticeWords, setNoticeWords] = useState('');

  const loadEntries = useCallback(async () => {
    try {
      const loadedEntries = await fetchHoldingEntries();

      setEntries(Array.isArray(loadedEntries) ? loadedEntries : []);
    } catch (loadProblem) {
      setEntries([]);
    }
  }, []);

  useEffect(() => {
    if (accessView.mayChangeSetup) {
      loadEntries();
    }
  }, [accessView.mayChangeSetup, loadEntries]);

  if (!accessView.mayChangeSetup) {
    return null;
  }

  function finishAttaching(chosenCustomer) {
    setEntryBeingAttached(null);
    setNoticeWords(`Attached to ${chosenCustomer.displayName || 'the customer'}. The waiting entry is kept in the record.`);
    loadEntries();
  }

  const waitingCount = entries ? entries.length : 0;

  return (
    <section className="cb-page cb-setup" id={HOLDING_CARD_ID}>
      <details className="cb-section" id={HOLDING_DETAILS_ID}>
        <summary>{`Waiting to be matched (${waitingCount})`}</summary>
        <div className="cb-setup-body">
          <p className="cb-sub">Things the other Butlers sent that matched no customer by email or address. Nothing here is thrown away.</p>
          {noticeWords ? <Notice tone="ok" icon="check">{noticeWords}</Notice> : null}
          {entries === null ? <QuietLine icon="clock">Loading</QuietLine> : null}
          {entries !== null && entries.length === 0 ? <QuietLine icon="check">Nothing is waiting.</QuietLine> : null}
          <ul className="cb-list-rows">
            {(entries || []).map((oneEntry) => (
              <SuiteEntryRow key={oneEntry.id} entry={oneEntry}>
                {oneEntry.holdingReason ? <div className="cb-item-meta">{oneEntry.holdingReason}</div> : null}
                <button type="button" className="cb-link-btn" onClick={() => setEntryBeingAttached(oneEntry)}>
                  <Icon name="link" /> Attach to a customer
                </button>
              </SuiteEntryRow>
            ))}
          </ul>
        </div>
      </details>
      {entryBeingAttached ? (
        <AttachSheet entry={entryBeingAttached} onAttached={finishAttaching} onClose={() => setEntryBeingAttached(null)} />
      ) : null}
    </section>
  );
}

export default SuiteTimelinePanel;
